/**
 * cssVariables.ts — Flattens the design tokens into CSS custom properties.
 *
 * Colors are emitted as space-separated RGB channels (e.g. `37 99 235`) so Tailwind's
 * `rgb(var(--color-x) / <alpha-value>)` form keeps supporting opacity modifiers (`/20`).
 * The generated `:root` map is what plain `.css` files (OnboardingPage.css, index.css,
 * App.css) reference via `var(--color-*)`, keeping a single source of truth.
 */
import { tokens, RAMP_STEPS, type ColorRamp } from './tokens';

/** Convert a `#rrggbb` / `#rgb` hex string into `"r g b"` channels. */
export function hexToRgbChannels(hex: string): string {
  let value = hex.trim().replace('#', '');
  if (value.length === 3) {
    value = value
      .split('')
      .map(c => c + c)
      .join('');
  }
  const int = Number.parseInt(value, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `${r} ${g} ${b}`;
}

function rampVars(prefix: string, ramp: ColorRamp): Record<string, string> {
  const out: Record<string, string> = {};
  for (const step of RAMP_STEPS) {
    out[`${prefix}-${step}`] = hexToRgbChannels(ramp[step]);
  }
  return out;
}

/** Build the full `:root` custom-property map from tokens. */
export function buildRootVars(): Record<string, string> {
  const vars: Record<string, string> = {};

  // Semantic ramps: --color-success-500, --color-primary-600, ...
  for (const [name, ramp] of Object.entries(tokens.ramp)) {
    Object.assign(vars, rampVars(`--color-${name}`, ramp));
  }
  // Single-value brand alias: --color-primary === primary-600
  vars['--color-primary'] = hexToRgbChannels(tokens.ramp.primary[600]);

  // Status ramps: --color-status-preArrival-500, ...
  for (const [name, ramp] of Object.entries(tokens.status)) {
    Object.assign(vars, rampVars(`--color-status-${name}`, ramp));
  }

  // Flat semantic colors: --color-surface, --color-ink, --color-border, ...
  for (const [name, hex] of Object.entries(tokens.color)) {
    const kebab = name.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    vars[`--color-${kebab}`] = hexToRgbChannels(hex);
  }

  // Chart palette: --color-chart-success, ...
  for (const [name, hex] of Object.entries(tokens.chart)) {
    vars[`--color-chart-${name}`] = hexToRgbChannels(hex);
  }

  // Radii: --radius-md, ...
  for (const [name, val] of Object.entries(tokens.radii)) {
    vars[`--radius-${name === 'DEFAULT' ? 'base' : name}`] = val;
  }

  // Transition durations/easings: --duration-normal, --ease-spring, ...
  for (const [name, val] of Object.entries(tokens.transition.duration)) {
    vars[`--duration-${name}`] = val;
  }
  for (const [name, val] of Object.entries(tokens.transition.easing)) {
    vars[`--ease-${name}`] = val;
  }

  // Shadows: --shadow-card, ...
  for (const [name, val] of Object.entries(tokens.shadow)) {
    vars[`--shadow-${name}`] = val;
  }

  return vars;
}

/**
 * Dark-mode overrides for the *semantic surface group* only. The numbered ramps stay put
 * (components opt into dark via Tailwind `dark:` variants); these flip the new semantic
 * surface/ink/border tokens so `bg-surface` / `text-ink` behave in dark mode once adopted.
 */
export function buildDarkVars(): Record<string, string> {
  const { ramp } = tokens;
  return {
    '--color-bg': hexToRgbChannels(ramp.neutral[900]),
    '--color-surface': hexToRgbChannels(ramp.neutral[800]),
    '--color-surface-muted': hexToRgbChannels(ramp.neutral[900]),
    '--color-surface-alt': hexToRgbChannels(ramp.neutral[700]),
    '--color-border': hexToRgbChannels(ramp.neutral[700]),
    '--color-border-strong': hexToRgbChannels(ramp.neutral[600]),
    '--color-ink': hexToRgbChannels(ramp.neutral[50]),
    '--color-ink-body': hexToRgbChannels(ramp.neutral[200]),
    '--color-ink-muted': hexToRgbChannels(ramp.neutral[400]),
    '--color-ink-subtle': hexToRgbChannels(ramp.neutral[500]),
  };
}
